/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import _ from 'lodash'
import useRootStore from '@store/useRootStore'
import { PickList, UserPickWrap } from '@views/Home/index.style'
import { ListView } from 'antd-mobile'
import { AdvButton } from '@components/Button'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON } from '@constants/index'
import { SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import PopupUtils from '@utils/popup-utils'
import { Schema$UserPickMatch } from '@interface/user-pick'
import { EXCEPTIONAL_UNIQUE_ID } from '@v2/constants/common'
import { PAGE_TYPE } from '@interface/common'
import { UserPickMatchInfo } from '@views/Home/UserPick/Components/UserPickMatchInfo'
import { UserPickProfileGroup } from '@views/Home/UserPick/Components/UserPickProfileGroup'
import { FavoriteButton } from '@components/Button/FavoriteButton'
import * as Utils from '@utils/utils'
import { TOAST_TYPE } from '@utils/utils'
import PageLoading from '@components/PageLoading'
import classNames from 'classnames'

const dataSource = new ListView.DataSource({
  rowHasChanged: (row1, row2) => row1 !== row2,
})

interface UserPickListProps {
  className?: string
  pageType: PAGE_TYPE
  userPromiseIds: string[]
}

export const UserPickMatchList: React.FC<UserPickListProps> = observer((props: UserPickListProps) => {
  const { userPickStore, matchupStore } = useRootStore()
  const { className, pageType, userPromiseIds } = props

  const {
    userPickMatches,
    userPickMatchesHasMore,
    filteredUserPickMatches,
    filteredUserPickMatchHasMore,
    isMatchPackageLoading,
    isSpecificUsersMatchesLoading,
  } = userPickStore
  const { majorUniqueID } = matchupStore

  const fetchPickMatches = (reset = false) => {
    userPickStore.fetchUserPickMatches(userPromiseIds, reset).catch(console.error)
  }

  const fetchFilteredMatches = () => {
    userPickStore.fetchUserPickFilteredMatches().catch(console.error)
  }

  const isStartedMatchShow = (): boolean => {
    return majorUniqueID === EXCEPTIONAL_UNIQUE_ID.STARTED_USER_PICK
      || majorUniqueID === EXCEPTIONAL_UNIQUE_ID.CLOSED_USER_PICK
  }

  const onClickFavoriteMatch = async (pickMatch: Schema$UserPickMatch): Promise<void> => {
    const { matchId, sportId, dateOfMatch } = pickMatch
    if (isStartedMatchShow() || Utils.isStartedMatch(dateOfMatch)) {
      Utils.showToast(TOAST_TYPE.INFO, '현재 찜할 수 없는 경기입니다.')
      return
    }
    const result = await matchupStore.toggleFavoriteMatch({ matchId, sportId, dateOfMatch })
    userPickStore.updateMatchFavorite(result, matchId)
  }

  const onClickUserPickDetail = (pickMatch: Schema$UserPickMatch): void => {
    userPickStore.setSelectedUserPickMatch(pickMatch)
    PopupUtils.showUserPickList()
  }

  const getFavoriteBtnClass = (pickMatch: Schema$UserPickMatch): string => {
    if (pickMatch.isFavoriteMatch) {
      return 'active ani'
    }
    if (isStartedMatchShow()) {
      return 'disable'
    }
    return ''
  }
  const renderRow = (row: Schema$UserPickMatch) => {
    const favoriteMatchButtonClass = getFavoriteBtnClass(row)
    return (
      <UserPickWrap>
        <div className="pick-item">
          <div className="tournament-info">
            {SPORT_ID_ICON_MAP[row.sportId]}{row.tournamentName}
          </div>

          <AdvButton
            width={'100%'}
            contents={<UserPickMatchInfo matchInfo={row} small vertical/>}
            className="pick-match-info-page"
            onClick={() => onClickUserPickDetail(row)}
          />

          <div className="sub-info company-pick">
            <span className="line"/>
            <UserPickProfileGroup users={row.userPickUsers} />
            <FavoriteButton
              id="userPickFavoriteBtn"
              className={favoriteMatchButtonClass}
              onClick={() => onClickFavoriteMatch(row)}
            />
          </div>
        </div>
      </UserPickWrap>
    )
  }

  const renderFooter = () => {
    let footer
    if (isMatchPackageLoading || isSpecificUsersMatchesLoading) {
      footer = <PageLoading/>
    } else if (_.size(userPickMatches) > 0 || pageType === PAGE_TYPE.USER_PICK_MATCH_FILTER) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.USER_PICK_LIST}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const endReachedHandler = async () => {
    if (pageType === PAGE_TYPE.USER_PICK_MATCH_FILTER && filteredUserPickMatchHasMore) {
      await fetchFilteredMatches()
    } else if (userPickMatchesHasMore) {
      await fetchPickMatches()
    }
  }

  const pickListClass = classNames(
    className,
    { 'pc-pick-list': !isMobile},
    { 'is-loading': isMatchPackageLoading || isSpecificUsersMatchesLoading},
  )

  const pickMatches = pageType === PAGE_TYPE.USER_PICK_MATCH_FILTER ? filteredUserPickMatches : userPickMatches
  return (
    <PickList className={pickListClass}>
      <ListView
        dataSource={dataSource.cloneWithRows(pickMatches ?? [])}
        renderRow={renderRow}
        scrollRenderAheadDistance={500}
        renderFooter={renderFooter}
        onEndReached={endReachedHandler}
        onEndReachedThreshold={10}
        initialListSize={9}
        pageSize={9}
        useBodyScroll={isMobile}/>
    </PickList>
  )
})
